import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  DISSOLVE_TYPES,
  MAX_DESATURATE_VARIATIONS,
  ditherColorChoices,
  dissolvePattern,
  rowsForRopeSpan,
} from "../lib/dissolve";
import { normalizeHex } from "../lib/harmonicPalette";
import { formatInches, strungInchesFromRope } from "../lib/knotCalc";

function sameColor(a, b) {
  const left = normalizeHex(a) || String(a || "").toLowerCase();
  const right = normalizeHex(b) || String(b || "").toLowerCase();
  return left === right;
}

const DissolvePanel = ({
  pattern,
  cols,
  rows,
  defaultColor,
  keptRows = null,
  onDissolve,
}) => {
  const [dissolveText, setDissolveText] = useState("6");
  const [type, setType] = useState("desaturate");
  const [variationText, setVariationText] = useState("3");
  const [maxBlockText, setMaxBlockText] = useState("");
  const [strengthText, setStrengthText] = useState("100");
  const [seedText, setSeedText] = useState("1");
  const [ditherColor, setDitherColor] = useState(null);
  const [historyCount, setHistoryCount] = useState(0);
  const historyRef = useRef([]);
  // Parent updates from Apply/Back should not wipe the pass history.
  const skipClears = useRef(0);

  useEffect(() => {
    if (skipClears.current > 0) {
      skipClears.current -= 1;
      return () => {
        skipClears.current += 1;
      };
    }
    if (historyRef.current.length === 0) return;
    historyRef.current = [];
    setHistoryCount(0);
  }, [pattern, cols, rows]);

  const dissolveInches = parseFloat(dissolveText);
  const strungInches = strungInchesFromRope(dissolveInches);
  const dissolveRows = rowsForRopeSpan(dissolveInches);
  const totalRows = Math.max(0, rows) + dissolveRows;

  const selected = useMemo(
    () => DISSOLVE_TYPES.find((item) => item.id === type) || DISSOLVE_TYPES[0],
    [type]
  );

  const ditherChoices = useMemo(
    () => ditherColorChoices(pattern, cols, rows, defaultColor),
    [pattern, cols, rows, defaultColor]
  );
  const fadeColor =
    ditherChoices.colors.find((color) => sameColor(color, ditherColor)) ||
    ditherChoices.dominant;

  const apply = () => {
    if (dissolveRows < 1) return;
    const strength = parseFloat(strengthText);
    historyRef.current.push({
      pattern: Array.isArray(pattern) ? pattern.slice() : [],
      cols,
      rows,
      keptRows,
    });
    setHistoryCount(historyRef.current.length);
    skipClears.current += 1;
    onDissolve({
      ...dissolvePattern({
        pattern,
        cols,
        rows,
        defaultColor,
        dissolveRows,
        type: selected.id,
        seed: parseInt(seedText, 10) || 1,
        maxBlock: maxBlockText === "" ? undefined : parseInt(maxBlockText, 10),
        variations: parseInt(variationText, 10),
        strength: Number.isFinite(strength) ? strength / 100 : 1,
        groundColor: selected.id === "dither" ? fadeColor : undefined,
      }),
      keptRows: rows,
    });
  };

  const back = () => {
    const previous = historyRef.current.pop();
    if (!previous) return;
    setHistoryCount(historyRef.current.length);
    skipClears.current += 1;
    onDissolve(previous);
  };

  return (
    <div className="dissolve-form">
      <section className="pg-section">
        <h3 className="pg-section__title">Length</h3>
        <div className="pg-field-row">
          <div className="workbench__field pg-field--narrow">
            <label htmlFor="dissolve-span-length">Dissolve length (rope in)</label>
            <input
              id="dissolve-span-length"
              type="number"
              min="0.1"
              step="0.25"
              value={dissolveText}
              onChange={(event) => setDissolveText(event.target.value)}
              className="number-input number-input--compact"
            />
          </div>
        </div>
        <p className="dissolve-copy">
          {dissolveRows > 0
            ? `${dissolveRows} rows (${formatInches(
                strungInches
              )} in strung). ${totalRows} rows total.`
            : "Enter a dissolve length."}
        </p>
      </section>

      <section className="pg-section">
        <h3 className="pg-section__title">Dissolve</h3>
        <div className="workbench__field">
          <label htmlFor="dissolve-type">Type</label>
          <select
            id="dissolve-type"
            className="select-input"
            value={selected.id}
            onChange={(event) => setType(event.target.value)}
          >
            {DISSOLVE_TYPES.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
        <p className="dissolve-copy">{selected.detail}</p>

        {type === "desaturate" && (
          <div className="workbench__field pg-field--narrow">
            <label htmlFor="dissolve-variations">Variations</label>
            <input
              id="dissolve-variations"
              type="number"
              min="1"
              max={MAX_DESATURATE_VARIATIONS}
              step="1"
              value={variationText}
              onChange={(event) => setVariationText(event.target.value)}
              className="number-input number-input--compact"
            />
          </div>
        )}

        {type === "fidelity" && (
          <div className="workbench__field pg-field--narrow">
            <label htmlFor="dissolve-block">Coarsest block</label>
            <input
              id="dissolve-block"
              type="number"
              min="1"
              max={cols}
              step="1"
              placeholder={String(Math.min(cols, 4))}
              value={maxBlockText}
              onChange={(event) => setMaxBlockText(event.target.value)}
              className="number-input number-input--compact"
            />
          </div>
        )}

        {type === "dither" && (
          <div className="workbench__field">
            <label id="dissolve-dither-color-label">Fade into</label>
            <div
              className="dissolve-colors"
              role="radiogroup"
              aria-labelledby="dissolve-dither-color-label"
            >
              {ditherChoices.colors.map((color) => {
                const active = sameColor(color, fadeColor);
                const hex = normalizeHex(color) || String(color);
                const mostCommon = sameColor(color, ditherChoices.dominant);
                const name = mostCommon ? `${hex}, most common` : hex;
                return (
                  <button
                    key={hex}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    aria-label={name}
                    title={name}
                    className={`dissolve-colors__swatch${
                      active ? " is-active" : ""
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setDitherColor(color)}
                  />
                );
              })}
            </div>
          </div>
        )}

        {type === "corruption" && (
          <div className="pg-field-row">
            <div className="workbench__field pg-field--narrow">
              <label htmlFor="dissolve-strength">Amount (%)</label>
              <input
                id="dissolve-strength"
                type="number"
                min="0"
                max="100"
                step="1"
                value={strengthText}
                onChange={(event) => setStrengthText(event.target.value)}
                className="number-input number-input--compact"
              />
            </div>
            <div className="workbench__field pg-field--narrow">
              <label htmlFor="dissolve-seed">Variation</label>
              <input
                id="dissolve-seed"
                type="number"
                step="1"
                value={seedText}
                onChange={(event) => setSeedText(event.target.value)}
                className="number-input number-input--compact"
              />
            </div>
          </div>
        )}

        <div className="pg-section__actions">
          <button
            type="button"
            className="button button--primary"
            onClick={apply}
            disabled={dissolveRows < 1}
          >
            Apply dissolve
          </button>
          <button
            type="button"
            className="button"
            onClick={back}
            disabled={historyCount < 1}
          >
            Back
          </button>
        </div>
      </section>
    </div>
  );
};

export default DissolvePanel;
